import React, { useState, useContext } from 'react';
import styled from 'styled-components';
import { t } from '@lingui/macro';
import { Trans } from '@lingui/react';
import { cloneDeep } from 'lodash';
import MButton from '@material-ui/core/Button';
import Tooltip from '@material-ui/core/Tooltip';
import PrivateIcon from '@material-ui/icons/Lock';
import PublicIcon from '@material-ui/icons/Public';
import HiddenIcon from '@material-ui/icons/VisibilityOff';
import { Body, Header, Modal } from '@organisms/modals/base';
import checkbox from '@components/form/checkbox/Checkbox';
import { H4 } from '@components/typography/Headings';
import { Box, Flex } from '@components/layout/Grid';
import { snackbarVar } from '@lib/apollo/policies';
import TextWithIcon from '@components/TextWithIcon';
import PlaylistModal from '../organisms/modals/PlaylistModal';
import useLoginModal from '@lib/hooks/useLoginModal';
import {
  useAddVideoToPlaylistMutation,
  useRemoveVideoFromPlaylistMutation,
  UserPlaylistsDocument,
  useUserPlaylistsQuery,
} from '@gql/generated';
import { UserContext } from '@lib/contexts/UserProvider';
import AnalyticsManager from '@lib/analytics/manager';

const Checkbox = styled(checkbox)`
  margin-right: 15px;

  .MuiCheckbox-root.MuiCheckbox-root {
    .MuiSvgIcon-root {
      /* in build some weird css issue, a rule adds opacity: 0, padding 9px */
      padding: 0;
      opacity: 1;
    }
  }
`;

const EnhancedBody = styled(Body)`
  padding-right: 0;
  max-height: 300px;
  overflow: auto;
  @media (max-width: ${({ theme }) => `${theme.viewports.tablet}px`}) {
    flex: 1;
    max-height: inherit;
  }
`;

const EnhancedModal = styled(Modal)`
  @media (max-width: ${({ theme }) => `${theme.viewports.tablet}px`}) {
    &&& {
      display: flex;
      flex-direction: column;
    }
  }
`;

const ModalButton = styled(MButton)`
  &&& {
    text-transform: uppercase;
    font-size: 13px;
    font-weight: 600;
    width: 100%;
    border: none;
    border-top: 1px solid #eee;
    padding: 20px;
    background-color: #fff;
  }
`;

const ICON = visibility => {
  switch (visibility) {
    case 'private':
      return (
        <Tooltip
          title={
            <Trans id="button_add_to_playlist.private" message="private" />
          }
          placement="top-start"
        >
          <PrivateIcon style={{ fillOpacity: 0.6 }} />
        </Tooltip>
      );
    case 'public':
      return (
        <Tooltip
          title={<Trans id="button_add_to_playlist.public" message="public" />}
          placement="top-start"
        >
          <PublicIcon style={{ fillOpacity: 0.6 }} />
        </Tooltip>
      );
    case 'hidden':
      return (
        <Tooltip
          title={<Trans id="button_add_to_playlist.hidden" message="hidden" />}
          placement="top-start"
        >
          <HiddenIcon style={{ fillOpacity: 0.6 }} />
        </Tooltip>
      );

    default:
      return <HiddenIcon style={{ fillOpacity: 0.6 }} />;
  }
};

const PAGE = 1;
const PERPAGE = 12;

const ButtonAddToPlaylist = props => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [fetching, setFetching] = useState(false);
  const [
    loginModalContent,
    { isOpen, openModal: openLoginModal },
  ] = useLoginModal({
    callBack() {
      // TODO: handle this
      // we need to convert playlist to hook style first
    },
  });

  // this should be probably globalized throught context
  // but what about perf?
  const [currentUser] = useContext(UserContext);

  // user is set to null when not authenticated, its undefined initially
  const isAuthenticated = currentUser?.id;

  const DynamicComponent = props.tag;
  const {
    loading,
    error,
    data,
    fetchMore,
    refetch: refetchPlaylists,
  } = useUserPlaylistsQuery({
    ssr: false,
    variables: {
      id: null,
      page: PAGE,
      perPage: PERPAGE,
    },
  });

  const [addVideoToPlaylist] = useAddVideoToPlaylistMutation();
  const [removeVideoFromPlaylist] = useRemoveVideoFromPlaylistMutation();

  const handleAddVideoToPlaylist = playlistId => {
    AnalyticsManager().sendVideoAddToPlaylistEvent({
      video_id: props.videoId,
      playlist_id: playlistId,
    });
    // TODO PLAYLIST: handle errors
    return addVideoToPlaylist({
      variables: {
        videoId: props.videoId,
        playlistId: playlistId,
      },
      optimisticResponse: () => {
        return {
          addVideoToPlaylist: { playlist: { id: playlistId } },
        };
      },
      update: proxy => {
        const newVideo = { id: props.videoId, __typename: 'Video' };
        const { userPlaylists: _userPlaylists } = proxy.readQuery({
          query: UserPlaylistsDocument,
          variables: { id: null, perPage: PERPAGE, page: PAGE },
        });
        const userPlaylists = cloneDeep(_userPlaylists);
        userPlaylists.data = userPlaylists.data.map(playlist => {
          if (playlist.id === playlistId) {
            playlist.videos.push(newVideo);
          }
          return playlist;
        });
        proxy.writeQuery({
          query: UserPlaylistsDocument,
          data: { userPlaylists },
          variables: { perPage: PERPAGE, page: PAGE },
        });
      },
    }).then(() => {
      snackbarVar({
        isSnackBarOpen: true,
        snackBarMessage: t`snackbars.added_video_playlist`,
        goTo: 'PLAYLIST',
        id: playlistId,
      });
    });
  };

  const handleRemoveVideoFromPlaylist = playlistId => {
    // TODO PLAYLIST: HANDLE ERRORS
    return removeVideoFromPlaylist({
      variables: {
        videoId: props.videoId,
        playlistId,
      },
      optimisticResponse: () => {
        return {
          removeVideoFromPlaylist: {
            playlist: {
              id: playlistId,
            },
          },
        };
      },
      update: proxy => {
        const { userPlaylists: _userPlaylists } = proxy.readQuery({
          query: UserPlaylistsDocument,
          variables: { id: null, perPage: PERPAGE, page: PAGE },
        });
        const userPlaylists = cloneDeep(_userPlaylists);
        const mutatedPlaylist = userPlaylists.data.find(
          playlist => playlist.id === playlistId
        );
        const videoIndex = mutatedPlaylist.videos.findIndex(
          video => video.id === props.videoId
        );
        mutatedPlaylist.videos.splice(videoIndex, 1);
        userPlaylists.data = userPlaylists.data.map(playlist => {
          if (playlist.id === playlistId) {
            return mutatedPlaylist;
          }
          return playlist;
        });
        proxy.writeQuery({
          query: UserPlaylistsDocument,
          data: { userPlaylists },
          variables: { perPage: PERPAGE, page: PAGE },
        });
      },
    }).then(() => {
      snackbarVar({
        isSnackBarOpen: true,
        goTo: 'PLAYLISTS',
        snackBarMessage: t`snackbars.removed_video_playlist`,
        id: playlistId,
      });
    });
  };

  if (loading || error) {
    return (
      <DynamicComponent disabled={loading}>
        <Trans id="metadata.please_wait" message={`Please wait...`} />
      </DynamicComponent>
    );
  }

  const userPlaylists = data?.userPlaylists;

  const handleAddToPlaylist = openPlaylistModal => {
    if (!userPlaylists || !isAuthenticated) {
      openLoginModal();
    } else if (userPlaylists.totalCount === 0) {
      openPlaylistModal();
    } else {
      setModalOpen(true);
    }
  };

  // const playlistsWithVideo = userPlaylists.data.filter(playlist =>
  //   playlist.videos.some(video => video.id === props.videoId)
  // );

  const handleShowMore = async () => {
    setFetching(true);
    fetchMore({
      variables: {
        page: userPlaylists.page + 1,
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) return prev;
        const { data: newData, ...rest } = fetchMoreResult.userPlaylists;
        setFetching(false);
        return {
          userPlaylists: {
            data: [...prev.userPlaylists.data, ...newData],
            ...rest,
          },
        };
      },
    });
  };

  return (
    <>
      <PlaylistModal
        onModalClose={props.onModalClose}
        onCompletedStorePlaylist={async playlistId => {
          await handleAddVideoToPlaylist(playlistId);
          return refetchPlaylists();
        }}
      >
        {openPlaylistModal => (
          <React.Fragment>
            <DynamicComponent
              onClick={() => {
                handleAddToPlaylist(openPlaylistModal);
              }}
            >
              <TextWithIcon icon="ygb-icon-playlist" iconSize="15px">
                <Trans id="menu_add_playlist.add" message="Add to Playlist" />
              </TextWithIcon>
            </DynamicComponent>
            {userPlaylists && isModalOpen ? (
              <EnhancedModal
                open={true}
                handleClose={() => setModalOpen(false)}
                data-testid="Modal--AddVideoToPlaylist"
              >
                <Header
                  handleClose={() => setModalOpen(false)}
                  paddingBottom={10}
                >
                  <Box as={H4} mt="0px" mb="0px">
                    <Trans
                      id="menu_add_playlist.add"
                      message={`Add to Playlist`}
                    />
                  </Box>
                </Header>
                <EnhancedBody my={10}>
                  {userPlaylists.data.map(playlist => {
                    const isVideoInPlaylist = playlist.videos.some(
                      video => video.id === props.videoId
                    );
                    return (
                      <Flex
                        key={playlist.id}
                        justifyContent={'space-between'}
                        alignItems={'center'}
                        my={'5px'}
                        pr={['25px', '25px', '50px']}
                        data-testid="PlaylistModal--Playlist"
                      >
                        <Checkbox
                          onChange={() => {
                            isVideoInPlaylist
                              ? handleRemoveVideoFromPlaylist(playlist.id)
                              : handleAddVideoToPlaylist(playlist.id);
                          }}
                          isChecked={isVideoInPlaylist}
                          data-testid="PlaylistModal--VideoInPlaylist"
                        >
                          {playlist.title}
                        </Checkbox>

                        {ICON(playlist.visibility)}
                      </Flex>
                    );
                  })}
                  {userPlaylists.totalCount / PERPAGE > userPlaylists.page && (
                    <Flex
                      alignItems={'center'}
                      justifyContent={'center'}
                      my={'5px'}
                      pr={['25px', '25px', '50px']}
                    >
                      <MButton
                        onClick={handleShowMore}
                        style={{ fontSize: '13px' }}
                        disabled={fetching}
                      >
                        Show More
                      </MButton>
                    </Flex>
                  )}
                </EnhancedBody>

                <ModalButton onClick={openPlaylistModal}>
                  <TextWithIcon icon="ygb-icon-playlist" iconSize="15px">
                    <Trans
                      id="menu_add_playlist.create"
                      message={'Create new Playlist'}
                    />
                  </TextWithIcon>
                </ModalButton>
              </EnhancedModal>
            ) : null}
          </React.Fragment>
        )}
      </PlaylistModal>
      {isOpen ? loginModalContent : null}
    </>
  );
};

export default ButtonAddToPlaylist;
